// import { Button } from 'src/components/Button';
import { Status } from '../components/status/Status';
import React, {
  PropsWithoutRef,
  useCallback, useEffect,
  // useEffect,
  useMemo,
  useState,
} from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

import { Table } from 'src/components/table/Table';
// import { FiltersList } from '../filters/FiltersList';
// import { CreatedAt, CardStatus, CardType, CardBrand, PersonStatus } from '../types';

import { cardsApi } from 'src/api';
import { StatusBadge, StatusBadgeType } from 'src/components/StatusBadge';
import { CardStatus } from 'src/api/client/models';
import ccBrandById from 'src/utils/cardBrand';
import { ReactComponent as DotsIcon } from 'src/assets/icons/dots.svg';
import { useForm } from 'react-hook-form';
import { RowsPerPageOptionType } from 'src/domain/access-management/user-management/tabs/users/UsersList';
import { LoadingSwitch } from 'src/components/Loading';
import { LoadMoreButton } from 'src/components/list/LoadMoreButton';
import { PageSize } from 'src/components/list/PageSize';

export const statusBadge = (status: CardStatus): StatusBadgeType => {
  switch (status) {
    case CardStatus.Active:
      return StatusBadgeType.SUCCESS;
    case CardStatus.Inactive:
    case CardStatus.Closed:
      return StatusBadgeType.SECONDARY;
    case CardStatus.Lost:
    case CardStatus.Stolen:
    case CardStatus.Fraud:
    case CardStatus.Blocked:
      return StatusBadgeType.DANGER;
    default:
      return StatusBadgeType.PRIMARY;
  }
};

export interface CardsTableProps
  extends PropsWithoutRef<JSX.IntrinsicElements['div']> {
  activeCards?: boolean;
  hasStatusFilter: boolean;
  personId?: string;
}

const ROW_PER_PAGE_OPTIONS: RowsPerPageOptionType[] = [
  {
    value: 20,
    label: 20,
  },
  {
    value: 50,
    label: 50,
  },
  {
    value: 100,
    label: 100,
  },
];

export const CardsTable = React.forwardRef<HTMLDivElement, CardsTableProps>(
  ({ children, activeCards, hasStatusFilter, personId, ...props }, ref) => {
    const { t } = useTranslation();
    const history = useHistory();

    const { watch, control } = useForm<{
      limit: { value: number; label: string };
    }>();

    const { limit } = watch();

    const [cursor, setCursor] = useState(0);
    const [id] = useState(Date.now());


    const {
      data,
      error,
      isLoading,
      isFetching,
      fetchNextPage,
      refetch,
      hasNextPage,
      remove,
    } = useInfiniteQuery(
      [id],
      () => {
        const req = {
          listCardsRequest: {
            cursor,
            limit: limit?.value || 20,
            personId: personId || undefined,
          },
        };

        return cardsApi.postListCard(req);
      },
      {
        enabled: !!limit,
        getNextPageParam: (lastPage) => lastPage?.cards?.length === limit?.value,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchIntervalInBackground: false,
      }
    );

    const handleLoadMore = () => {
      setCursor(cursor + limit?.value);
    };

    useEffect(() => {
      if (cursor > 0) {
        fetchNextPage();
      }
      if (cursor === 0) {
        remove();
        refetch();
      }
    }, [cursor, fetchNextPage, refetch]); // eslint-disable-line

    useEffect(() => {
      if (cursor === 0) {
        refetch();
      } else {
        setCursor(0);
      }
    }, [refetch, limit, id, setCursor]); // eslint-disable-line


    const tableData = useMemo(() => {
      if (!isLoading && !error && data) {
        // @ts-ignore
        return data.pages!.reduce((result, group) => {
          return [
            ...result,
            // @ts-ignore
            ...group.cards!.map((row) => [
              {
                value: (
                  <span className="text-ssm text-gray-900 font-semibold">
                    {row.cardholderName}
                  </span>
                ),
              },
              {
                value: (
                  <span className="text-ssm text-gray-700 font-semibold">
                    {t(`cards.table.cardType.${row.type?.toLowerCase()}`)}
                  </span>
                ),
              },
              {
                value: (
                  <span className="text-ssm text-gray-900 font-semibold uppercase">
                    {ccBrandById(row.maskedPan)}
                  </span>
                ),
              },
              {
                value: (
                  <span className="text-ssm text-gray-900 font-semibold">
                    {row.programName}
                  </span>
                ),
              },
              {
                value: row.lastFourDigits && (
                  <span className="inline-flex items-center text-ssm text-gray-900 font-semibold">
                    <DotsIcon className="mr-2" />
                    {row.lastFourDigits}
                  </span>
                ),
              },
              {
                value: (
                  <span className="text-ssm text-gray-900 font-semibold">
                    {row.proxyNumber}
                  </span>
                ),
              },
              {
                value: row.status && (
                  <StatusBadge variant={statusBadge(row.status)}>
                    {t(`cards.table.status.${row.status?.toLowerCase()}`)}
                  </StatusBadge>
                ),
              },
              {
                value: (
                  <span className="inline-block text-right text-xxs text-gray-700">
                    {moment.utc(row.createdAt).format('D MMM YYYY')}
                    <br />
                    at {moment.utc(row.createdAt).format('HH:mm')}
                  </span>
                ),
              },
            ]),
          ];
        }, [] as any[]);
      }

      return [];
    }, [data, t, isLoading, error]);

    const columns = [
      {
        value: (item: any) => item[0].value,
        label: t('cards.table.columns.cardholder'),
        width: '15%',
        id: '0',
      },
      {
        value: (item: any) => item[1].value,
        label: t('cards.table.columns.type'),
        id: '1',
      },
      {
        value: (item: any) => item[2].value,
        label: t('cards.table.columns.brand'),
        id: '2',
      },
      {
        value: (item: any) => item[3].value,
        label: t('cards.table.columns.program'),
        id: '3',
      },
      {
        value: (item: any) => item[4].value,
        label: t('cards.table.columns.lastFour'),
        id: '4',
      },
      {
        value: (item: any) => item[5].value,
        label: t('cards.table.columns.proxy'),
        id: '5',
      },
      {
        value: (item: any) => item[6].value,
        label: t('cards.table.columns.status'),
        id: '6',
      },
      {
        value: (item: any) => item[7].value,
        label: t('cards.table.columns.createdAt'),
        id: '7',
      },
    ];

    const navigateToCard = useCallback(
      (item, index) => {
        // @ts-ignore
        const spreadData = data.pages!.reduce((result, group) => {
          // @ts-ignore
          return [...result, ...group.cards];
        }, [] as any[]);

        console.log(item, index);

        history.push(`/cards/card-details/${spreadData![index].id}`);
      },
      [data]
    );

    return (
      <div ref={ref} {...props} className="flex flex-col h-full pt-2">
        {error?.status === 403 ? (
          <div className="mt-1 mx-3 mb-3 px-5 py-3 text-sm text-gray-900 bg-red-100 rounded-sm">
            {t('permissions.permissionDenied')}
          </div>
        ) : (
          <div>
            <Table
              items={tableData}
              loading={isLoading}
              columns={columns}
              withFilters={false}
              isHeaderFixed
              onRowClick={(item, index) => {
                navigateToCard(item, index);
              }}
            />

            <div className="flex items-center justify-between p-5 py-3 mt-auto -mt-px -mb-5 bg-white border-t border-gray-300">
              <div className="items-center hidden text-xs text-gray-700 md:flex">
                {t('list.pagination.results')}:
                <span className="ml-1">
                  <LoadingSwitch loading={isLoading}>
                    <span className="font-medium">{tableData?.length}</span>
                  </LoadingSwitch>
                </span>
              </div>
              <LoadMoreButton
                loading={isLoading || isFetching}
                onClick={handleLoadMore}
                disabled={!hasNextPage}
              />

              <PageSize
                control={control}
                options={ROW_PER_PAGE_OPTIONS}
                name={'limit'}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);
